// TODO:[] need to ensure label to be mandatory when its icon button
import cx from 'classnames';
import _ from 'lodash';
import React, { KeyboardEvent, MouseEvent } from 'react';
import { Icon, IconColor, IconID, IconSize } from '../icon';
import { LoadingIndicator } from 'packages/radiant-react/loading-indicator';
import { Colors, Typography } from 'packages/radiant-react/typography';
import styles from './button.module.scss';

export type ButtonType = 'primary' | 'secondary' | 'tertiary';
export type ButtonSize = 'xs' | 's' | 'm';
export type ButtonBackgroundType = 'light' | 'colored';
export type IconPlacement = 'left' | 'right';
export type BorderPacement = 'leftOnly' | 'rightOnly' | 'both';

const buttonSizeToTypographyVariantMap = {
  xs: 'footnote',
  s: 'footnote',
  m: 'body-normal',
};

const buttonSizeToIconSizeMap = {
  xs: IconSize.XSMALL,
  s: IconSize.SMALL,
  m: IconSize.LARGE,
};

const buttonWithTextSizeToIconSizeMap = {
  xs: IconSize.SMALL,
  s: IconSize.SMALL,
  m: IconSize.MEDIUM,
};

const buttonSizeClassMap = {
  xs: styles.sizeXs,
  s: styles.sizeS,
  m: styles.sizeM,
};

const buttonRadiusClassMap = {
  leftOnly: styles.leftOnly,
  rightOnly: styles.rightOnly,
  both: styles.both,
};

const buttonBackgroundClassMap = {
  light: styles.backgroundLight,
  colored: styles.backgroundColored,
};

export interface ButtonProps {
  /**
   * Label for ally, it is manatory for icon buttons
   */
  ariaLabel?: string;
  /**
   * ID for the button
   */
  id?: string;
  /**
   * className for the button
   */
  className?: string;
  /**
   * Text inside of button
   */
  text?: string;
  /**
   * Tooltip of button
   */
  tooltip?: string;
  /**
   * to enable / disable button
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Type of button
   * @default 'primary'
   */
  type?: ButtonType;
  /**
   * Size of button
   * @default 'm'
   */
  size?: ButtonSize;
  /**
   * check if saving in progress or not
   * @default false
   */
  isSaving?: boolean;
  /**
   * Id of the icon inside of the button
   */
  icon?: IconID;
  /**
   * iconClassname to override icon class
   */
  iconClassName?: string;
  /**
   * Placement of icon in respect to text
   * @default 'left'
   */
  iconPlacement?: IconPlacement;
  /**
   * Color of Icon
   * @default 'IconColor.TEXT_COLOR'
   */
  iconColor?: IconColor;
  /**
   * Check if button is selected or not
   * @default false
   */
  isSelected?: boolean;
  /**
   * background of the button
   */
  backgroundType?: ButtonBackgroundType;
  /**
   * Click handler
   * @default noop
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Double Click handler
   * @default noop
   */
  onDoubleClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Mouse down handler
   * @default noop
   */
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Key down handler
   * @default noop
   */
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  /**
   * Check if button should be expanded to full width or not
   * @default false
   */
  isFullWidth?: boolean;
  /**
   * Check if button should be truncated or not
   * @default false
   */
  isTextTruncated?: boolean;
  /**
   * Placement of button border-radius
   * @default 'both'
   */
  borderPlacement?: BorderPacement;
  /**
   * Ref for button to be called in case of DOM changes
   */
  buttonRef?: React.RefObject<HTMLButtonElement>;

  tabIndex?: number;

  /**
   * Clicking the button results in it being focused until a click occurs outside of the button.
   *
   * But for example the pagination buttons (next/previous) the focus should not persist after clicking
   * and moving the mouse out of the button.
   *
   * @default retainFocusOnClick is set to true to maintain compatibility with existing behavior.
   */
  retainFocusOnClick?: boolean;
  /**
   * Value of the notification badge.
   * Max displayed value will be 99.
   * @default: undefined.
   */
  badgeValue?: number;

  /**
   * Whether to show a dot badge instead of a numbered badge.
   * @default: false
   */
  showBadge?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  id,
  ariaLabel,
  text,
  tooltip,
  icon,
  iconPlacement = 'left',
  iconClassName,
  iconColor = IconColor.TEXT_COLOR,
  className,
  isSaving = false,
  isDisabled = false,
  isSelected = false,
  onClick,
  onDoubleClick,
  onKeyDown,
  size = 'm',
  type = 'primary',
  backgroundType = 'light',
  isFullWidth = false,
  isTextTruncated = true,
  borderPlacement = 'both',
  buttonRef,
  tabIndex,
  retainFocusOnClick = true,
  badgeValue = undefined,
  showBadge = false,
  ...restProps
}: ButtonProps) => {
  const buttonWrapperClassNames = cx(
    styles.buttonWrapper,
    {
      [styles.fullWidth]: isFullWidth,
    },
    className
  );

  const buttonClassNames = cx(
    styles.button,
    {
      [styles.buttonWithIcon]: !!icon, //
      [styles.buttonWithText]: !!text,
      [styles.buttonSelected]: isSelected,
      [styles.disabled]: isDisabled,
      [styles.fullWidth]: isFullWidth,
    },
    styles[type],
    buttonSizeClassMap[size],
    buttonBackgroundClassMap[backgroundType],
    buttonRadiusClassMap[borderPlacement]
  );

  const textClassNames = cx(styles.buttonText, {
    [styles.hidden]: isSaving,
    [styles.iconLeft]: !!icon && iconPlacement === 'left',
    [styles.iconRight]: !!icon && iconPlacement === 'right',
    [styles.truncateText]: isTextTruncated,
    /* Experimental style to conform with design
        this can be removed once dual intent button is moved to radiant */
    [styles.buttonWithTextAndBorderLeft]: !!text && !!icon && borderPlacement === 'leftOnly',
    [styles.buttonWithTextAndBorderRight]: !!text && !!icon && borderPlacement === 'rightOnly',
    /* Experimental style */
  });

  const iconClassNames = cx({
    [styles.hidden]: isSaving,
  });

  /**
   * this function is used to get the color of the loader, it depends on the button type
   * and button size and the button background type.
   */
  const getLoaderColor = () => {
    let loaderColor = 'gray';

    if (backgroundType === 'light') {
      if (type === 'primary') {
        loaderColor = 'white';
      } else if (type === 'secondary') {
        loaderColor = 'gray';
      } else {
        loaderColor = !!icon && !text ? 'gray' : 'blue';
      }
    } else {
      loaderColor = type === 'tertiary' ? 'white' : 'gray';
    }
    return loaderColor;
  };

  const getTextColor = () => {
    let textColor;

    if (backgroundType === 'light') {
      if (type === 'primary') {
        textColor = Colors.white;
      } else if (type === 'secondary') {
        textColor = Colors.base;
      } else {
        textColor = Colors.accent;
      }
    } else {
      textColor = type === 'tertiary' ? Colors.white : Colors.base;
    }
    return textColor;
  };

  const getIconSize = () => {
    const buttonContainsText = !!text;
    return buttonContainsText
      ? buttonWithTextSizeToIconSizeMap[size]
      : buttonSizeToIconSizeMap[size];
  };

  const renderLoader = () => {
    const loaderColor = getLoaderColor();

    return (
      isSaving && (
        <LoadingIndicator.Contextual
          className={cx(styles.loadingSpinner)}
          loaderColor={loaderColor as any}
        />
      )
    );
  };

  const renderText = () => {
    const variantName = buttonSizeToTypographyVariantMap[size];
    const textColor = getTextColor();

    return (
      !!text && (
        <Typography
          variant={variantName as any}
          className={textClassNames}
          color={textColor}
          // Button margin should be respected for cases like buttons with icon/s.
          ellipsis={{ rows: 1, allowMargin: true }}
        >
          {text}
        </Typography>
      )
    );
  };

  const handleOnClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled && onClick) {
      onClick(event);
    }
  };

  const handleOnDoubleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled && onDoubleClick) {
      onDoubleClick(event);
    }
  };

  const handleOnKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!isDisabled && onKeyDown) {
      onKeyDown(event);
    }
  };
  return (
    <div
      id={id}
      className={buttonWrapperClassNames}
      data-tooltip-content={tooltip}
      data-tooltip-position="bottom"
      data-tooltip-align="center"
    >
      <button
        type="button"
        className={buttonClassNames}
        onClick={handleOnClick}
        onDoubleClick={handleOnDoubleClick}
        onKeyDown={handleOnKeyDown}
        aria-label={ariaLabel || text}
        disabled={isDisabled}
        tabIndex={tabIndex || undefined}
        ref={buttonRef}
        onMouseLeave={(e) => {
          if (!retainFocusOnClick) {
            (e.target as HTMLButtonElement).blur();
          }
        }}
        {...restProps}
      >
        {renderLoader()}
        {icon && iconPlacement === 'left' && (
          <Icon
            id={icon as IconID}
            color={iconColor}
            size={getIconSize()}
            className={cx(iconClassNames, iconClassName)}
          />
        )}
        {renderText()}
        {icon && iconPlacement === 'right' && (
          <Icon
            id={icon as IconID}
            color={iconColor}
            size={getIconSize()}
            className={cx(iconClassNames, iconClassName)}
          />
        )}
        {/* Numbered Badge */}
        {!_.isUndefined(badgeValue) && (
          <span className={cx(styles.badge)}>
            {badgeValue && badgeValue < 100 ? badgeValue : 99}
          </span>
        )}

        {/* Simple Dot Badge */}
        {showBadge && _.isUndefined(badgeValue) && (
          <span className={cx(styles.badge, styles.dotBadge)} />
        )}
      </button>
    </div>
  );
};
